/**
 * ── THE CLOCK QUEUE: a clock tap survives the network and the tab ──────────
 *
 * Option B of the clock-reliability plan (api#1164, migration 0536). A clock-in
 * or clock-out is written to IndexedDB THE MOMENT IT IS TAPPED, with its own
 * request id and the device's tap time, and then sent to
 * POST /timesheets/clock-events — now if there is a network, later if not.
 *
 * The server makes replay safe, so this file never has to be clever about it:
 *   - one request id is applied ONCE; a replay returns the stored outcome;
 *   - the tap time is used when plausible, else server time;
 *   - a clock-out closes only the shift it NAMES, so a late replay can never
 *     close the hand's next shift.
 *
 * ── WHEN AN ITEM LEAVES THE QUEUE ──────────────────────────────────────────
 * Any response with a body is the server's answer and dequeues the item:
 * 2xx applied / already_done, 4xx refused. Only NO response (offline, timeout)
 * or a 5xx keeps it, and the flush then STOPS — taps are sent in the order they
 * were made, because a clock-out of a shift opened by a still-queued clock-in
 * has no shift id until that clock-in lands.
 *
 * ── A CLOCK-OUT OF A SHIFT THAT DOES NOT EXIST YET ─────────────────────────
 * Clock in offline, then clock out offline: the clock-out has no timesheet id.
 * It carries `after_request_id` instead, and when the clock-in is applied its
 * returned `timesheet_id` is written onto the waiting clock-out. If the
 * clock-in is refused, the clock-out that depended on it is dropped with it.
 *
 * ── WHERE IT LIVES ─────────────────────────────────────────────────────────
 * One array under `syncMeta['clockQueue']` — no IndexedDB version bump, and the
 * login tenant purge (clearAllStores) empties it with everything else, so a tap
 * can never be sent under the next person who signs in on the device. As a
 * second guard each item carries the user it was tapped by and is only sent
 * while that user's token is the one in storage.
 *
 * ── WHO FLUSHES ────────────────────────────────────────────────────────────
 * The tap itself; ClockInScreen on mount, on `online`, and every 30s while
 * anything is pending; and performSync (sync-engine), which the online detector
 * runs on reconnect — so a tap queued on ClockInScreen is sent even if the hand
 * has moved to another screen.
 */
import { api } from '../../api/client';
import { storage } from '../storage';
import { getDB } from './db';

export type ClockTapAction = 'clock_in' | 'clock_out';
export type ClockTapOutcome = 'applied' | 'already_done' | 'refused' | 'queued';

export interface QueuedClockTap {
  client_request_id: string;
  action: ClockTapAction;
  device_tapped_at: string;
  /** clock_out: the shift being closed. Null while it waits on a queued clock-in. */
  timesheet_id?: string | null;
  /** clock_out of a shift opened by a still-queued clock-in: that clock-in's request id. */
  after_request_id?: string | null;
  /** work_location, job_id, work_item_vertical/_id, lat, lng, note — as /clock-in takes them. */
  body: Record<string, unknown>;
  user_id: string;
  queued_at: string;
  attempts: number;
  last_error?: string | null;
}

export interface ClockTapResult {
  client_request_id: string;
  action: ClockTapAction;
  outcome: ClockTapOutcome;
  /** The server's `data` (outcome, replayed, time_source, applied_at, timesheet_id, detail). */
  data?: any;
  /** The refusal text, for outcome 'refused'. */
  error?: string;
  status?: number;
}

/**
 * A v4 UUID for a tap's request id. The browser's own generator, so this module
 * pulls in no ESM-only package (Jest here cannot parse `uuid`); getRandomValues
 * covers WebViews without randomUUID.
 */
export function newClockRequestId(): string {
  const c: Crypto | undefined = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
  if (c && typeof (c as any).randomUUID === 'function') return (c as any).randomUUID();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b); else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export const CLOCK_QUEUE_KEY = 'clockQueue';
export const CLOCK_QUEUE_EVENT = 'fieldiq:clock-queue';

function notify() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CLOCK_QUEUE_EVENT));
}

async function readQueue(): Promise<QueuedClockTap[]> {
  const db = await getDB();
  const v = await db.get('syncMeta', CLOCK_QUEUE_KEY);
  return Array.isArray(v) ? v : [];
}

async function writeQueue(items: QueuedClockTap[]): Promise<void> {
  const db = await getDB();
  await db.put('syncMeta', items, CLOCK_QUEUE_KEY);
  notify();
}

/** The user id in the stored token, or null. A guard, not authentication: the server checks the JWT. */
export async function currentTokenUserId(): Promise<string | null> {
  try {
    const token = await storage.getToken();
    const part = token?.split('.')[1];
    if (!part) return null;
    const pad = '='.repeat((4 - (part.length % 4)) % 4);
    const payload = JSON.parse(atob((part + pad).replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload?.userId === 'string' ? payload.userId : null;
  } catch {
    return null;
  }
}

/** Persist a tap. Resolves once it is durably in IndexedDB; rejects if the cache is unavailable. */
export async function enqueueClockTap(tap: Omit<QueuedClockTap, 'queued_at' | 'attempts'>): Promise<void> {
  const items = await readQueue();
  if (items.some((i) => i.client_request_id === tap.client_request_id)) return;
  items.push({ ...tap, queued_at: new Date().toISOString(), attempts: 0, last_error: null });
  await writeQueue(items);
}

export async function pendingClockTaps(userId: string | null | undefined): Promise<QueuedClockTap[]> {
  if (!userId) return [];
  try {
    return (await readQueue()).filter((i) => i.user_id === userId);
  } catch {
    return [];
  }
}

function toRequestBody(tap: QueuedClockTap): Record<string, unknown> {
  return {
    ...tap.body,
    client_request_id: tap.client_request_id,
    action: tap.action,
    device_tapped_at: tap.device_tapped_at,
    ...(tap.action === 'clock_out' ? { timesheet_id: tap.timesheet_id } : {}),
  };
}

/**
 * One POST of one tap. `answered: false` means no response or a 5xx — the tap
 * must stay queued. Any other response is the server's answer: 2xx is applied /
 * already_done, 4xx is refused.
 */
export async function sendClockTap(
  tap: QueuedClockTap,
): Promise<{ answered: true; result: ClockTapResult } | { answered: false; reason: string }> {
  try {
    const r: any = await api.timesheets.clockEvent(toRequestBody(tap));
    const data = r?.data?.data ?? null;
    return {
      answered: true,
      result: { client_request_id: tap.client_request_id, action: tap.action, outcome: data?.outcome ?? 'applied', data, status: r?.status },
    };
  } catch (err: any) {
    const status = err?.response?.status;
    if (typeof status !== 'number' || status >= 500) {
      return { answered: false, reason: typeof status === 'number' ? `HTTP ${status}` : (err?.message ?? 'no response') };
    }
    const body = err.response?.data;
    return {
      answered: true,
      result: {
        client_request_id: tap.client_request_id, action: tap.action, outcome: 'refused',
        data: body?.data, error: body?.error ?? `Request refused (${status})`, status,
      },
    };
  }
}

let inFlight: Promise<Map<string, ClockTapResult>> | null = null;

/**
 * Send what is queued, in order, for the signed-in user. Single-flight: a call
 * while a flush is running joins it. Returns the outcome of every tap that left
 * the queue on this pass, keyed by request id; a tap still queued has no entry.
 */
export function flushClockQueue(): Promise<Map<string, ClockTapResult>> {
  if (!inFlight) {
    inFlight = runFlush().finally(() => { inFlight = null; });
  }
  return inFlight;
}

async function runFlush(): Promise<Map<string, ClockTapResult>> {
  const results = new Map<string, ClockTapResult>();
  const userId = await currentTokenUserId();
  if (!userId) return results;

  let items = await readQueue();
  for (;;) {
    const tap = items.find((i) => i.user_id === userId);
    if (!tap) break;

    // A clock-out still waiting on a queued clock-in's shift id.
    if (tap.action === 'clock_out' && !tap.timesheet_id) {
      const waitingOn = tap.after_request_id && items.some((i) => i.client_request_id === tap.after_request_id);
      if (waitingOn) break; // its clock-in is ahead of it and has not landed
      // Its clock-in left the queue without opening a shift (refused): nothing to close.
      results.set(tap.client_request_id, {
        client_request_id: tap.client_request_id, action: tap.action, outcome: 'refused',
        error: 'The clock-in this clock-out belonged to was not recorded.',
      });
      items = items.filter((i) => i !== tap);
      await writeQueue(items);
      continue;
    }

    const sent = await sendClockTap(tap);
    if (!sent.answered) {
      // Not answered: keep it, and stop — order matters (see header).
      tap.attempts += 1;
      tap.last_error = sent.reason;
      await writeQueue(items);
      break;
    }
    const res = sent.result;

    if (tap.action === 'clock_in') {
      let opened: string | null = res.outcome === 'applied' ? (res.data?.timesheet_id ?? null) : null;
      // already_done: the hand was on the clock already (tapped in elsewhere),
      // and that shift is the one a waiting clock-out means. The event does not
      // name it, so ask for the open shift.
      if (res.outcome === 'already_done' && items.some((d) => d.after_request_id === tap.client_request_id)) {
        try {
          const s: any = await api.timesheets.status();
          const body = s?.data && typeof s.data === 'object' && 'data' in s.data ? s.data.data : s?.data;
          opened = body?.timesheet?.id ?? null;
        } catch {
          // Could not ask. Leave the clock-in queued too: its replay returns the
          // stored outcome and this question is asked again on the next flush.
          tap.attempts += 1;
          tap.last_error = 'open shift unknown';
          await writeQueue(items);
          break;
        }
      }
      // Hand the opened shift to any clock-out that was waiting on it; with
      // none, that clock-out falls to the 'refused' branch above.
      for (const dep of items) {
        if (dep.action === 'clock_out' && dep.after_request_id === tap.client_request_id && !dep.timesheet_id) {
          dep.timesheet_id = opened;
        }
      }
    }
    results.set(tap.client_request_id, res);
    items = items.filter((i) => i !== tap);
    await writeQueue(items);
  }
  return results;
}
